import React from 'react'
import {Box, HStack, Skeleton, StackProps, TextProps, useMultiStyleConfig, VStack, Text} from '@chakra-ui/react'
import {Picture} from '_uiComponents'
import {FormattedMessage} from 'react-intl'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {Link} from 'react-router-dom'
import {getProductAnalyticsInfo} from '_analytics/utils/analyticsUtils'
import {updateCloudinarySize} from '_utils'

interface RelatedProductsSelectorProps {
    productIds: Array<string>
    activeProductId?: string
    onRelatedProductClick: (relatedProductId: string, relatedProductDescription: string) => void
}

/**
 * @name RelatedProductsSelector
 *
 * @component
 *
 * @description
 * Renders the related products selector inside a PDP.
 *
 *
 * @returns {React.FC} - The rendered related products selector component.
 */
const RelatedProductsSelector: React.FC<RelatedProductsSelectorProps> = ({
    productIds,
    activeProductId,
    onRelatedProductClick,
}: RelatedProductsSelectorProps) => {
    const styles = useMultiStyleConfig('ProductRelatedSelector') as Record<string, StackProps & TextProps>

    const {data: relatedProducts, isLoading} = useProducts({
        parameters: {
            ids: productIds.join(','),
            allImages: false,
            expand: ['availability'],
            c_pdpRelatedProduct: true,
        },
    })

    const handleProductClick = (productId: string, productDescription: string) => {
        if (productId !== activeProductId) {
            onRelatedProductClick(productId, productDescription)
        }
    }

    if (isLoading) {
        return (
            <VStack {...styles.wrapper} data-testid="pdp-related-products">
                <Skeleton {...styles.titleSkeleton} />

                <HStack {...styles.productsContainer}>
                    {Array.from({length: productIds.length}, (_, index) => (
                        <Box {...styles.productWrapper} key={`related-product-skeleton-${index}`}>
                            <Box {...styles.productImageWrapper}>
                                <Skeleton {...styles.productImageSkeleton} />
                            </Box>
                            <Skeleton {...styles.productDescriptionSkeleton} />
                        </Box>
                    ))}
                </HStack>
            </VStack>
        )
    } else if (!relatedProducts?.data) {
        return undefined
    }

    return (
        <VStack {...styles.wrapper} data-testid="pdp-related-products">
            <Text {...styles.title}>
                <FormattedMessage id="label.pdp.related.products.title" defaultMessage={'Scopri altre dimensioni'} />
            </Text>

            <HStack {...styles.productsContainer}>
                {relatedProducts?.data?.map((product, index) => {
                    const {c_relatedProductDescription: productDescription} = product
                    const {activeVariation} = getProductAnalyticsInfo(product)
                    const {ID: productId} = activeVariation
                    const {url: productImageSrc = '', alt: productImageAlt = ''} = activeVariation?.images || activeVariation?.images?.large?.[0] || {}
                    const isSelected = activeProductId === productId

                    return (
                        <Box {...styles.productWrapper} data-testid={`related-product-${productId}`} key={`${productId}-${index}`}>
                            <Link
                                to={activeVariation?.pdpUrl}
                                onClick={() => handleProductClick(productId, productDescription)}
                                data-testid={`search-product-${productId}`}
                            >
                                <Box {...styles.productImageWrapper} {...(isSelected ? styles.productImageWrapperSelected : {})}>
                                    <Picture
                                        imagexs={updateCloudinarySize(productImageSrc, 400)}
                                        imagesm={updateCloudinarySize(productImageSrc, 400)}
                                        imagemd={updateCloudinarySize(productImageSrc, 400)}
                                        imagelg={updateCloudinarySize(productImageSrc, 250)}
                                        imagexl={updateCloudinarySize(productImageSrc, 150)}
                                        alt={productImageAlt}
                                        title={product.name}
                                        hasLazy={false}
                                        objectFit="cover"
                                        w="100%"
                                        h="100%"
                                        position="absolute"
                                        inset={0}
                                    />
                                </Box>
                            </Link>
                            <Text {...styles.productDescription}>{productDescription}</Text>
                        </Box>
                    )
                })}
            </HStack>
        </VStack>
    )
}

RelatedProductsSelector.displayName = 'RelatedProductsSelector'

export default RelatedProductsSelector
